// Shared form field resolver for public forms
const BASE_FIELDS = [
    { id: 'firstName', label: 'First Name', type: 'text', required: false, editable: true, locked: true },
    { id: 'lastName', label: 'Last Name', type: 'text', required: false, editable: true, locked: true },
    { id: 'email', label: 'Email', type: 'email', required: true, editable: true, locked: true },
    { id: 'phone', label: 'Phone', type: 'tel', required: true, editable: true, locked: true },
    { id: 'address', label: 'Address', type: 'text', required: true, editable: true, locked: true },
    { id: 'interestedProperty', label: 'Interested Property', type: 'multiselect', required: true, editable: true, locked: true, options: [] }
];

/**
 * Legacy field migration: convert old 'name' field to firstName/lastName in custom fields.
 * If a saved form config contains a 'name' field, it is automatically replaced with
 * firstName + lastName to match the current schema used by manual lead creation.
 */
function migrateLegacyNameField(sourceFields) {
    if (!Array.isArray(sourceFields)) return sourceFields;
    return sourceFields.filter(sf => {
        // Remove any explicit 'name' field (string or object) — it's now replaced by firstName/lastName base fields
        if (typeof sf === 'string') {
            return sf.trim().toLowerCase() !== 'name';
        }
        if (sf && typeof sf === 'object') {
            const id = (sf.id || sf.name || sf.label || sf.value || '').trim().toLowerCase();
            return id !== 'name';
        }
        return true;
    });
}

function toStartCase(str) {
    if (!str) return '';
    return String(str)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_\-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
        .join(' ');
}

function toCamelCase(label) {
    if (!label) return '';
    const cleaned = String(label).replace(/[^a-zA-Z0-9 _-]/g, ' ').trim();
    const parts = cleaned.split(/[_\-\s]+/).filter(Boolean);
    if (parts.length === 0) return '';
    return parts.map((p, idx) => {
        const lower = p.toLowerCase();
        if (idx === 0) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join('');
}

// sourceFields: array of FormField-like objects or strings
function resolveFormFields(sourceFields = []) {
    // Base fields cloned
    const base = BASE_FIELDS.map(f => ({ ...f }));

    if (!Array.isArray(sourceFields) || sourceFields.length === 0) return base;

    // Migrate legacy 'name' field to firstName/lastName
    const migratedFields = migrateLegacyNameField(sourceFields);

    const extras = [];
    migratedFields.forEach(sf => {
        if (typeof sf === 'string') {
            const raw = String(sf).trim();
            if (!raw) return;
            const id = toCamelCase(raw);
            if (base.some(b => b.id === id)) return;
            extras.push({ id, label: toStartCase(raw), type: 'text', required: false, editable: true, locked: false });
        } else if (sf && typeof sf === 'object') {
            // sf may contain { name, label, id, type, required, editable, locked } or { value, formName, formId }
            const rawName = sf.name || sf.label || sf.value || sf.id || '';
            const label = sf.label ? String(sf.label).trim() : toStartCase(rawName);
            const id = sf.id ? String(sf.id).trim() : toCamelCase(rawName);
            if (!id) return;
            if (base.some(b => b.id === id)) return;
            extras.push({
                id,
                label,
                type: sf.type || 'text',
                required: !!sf.required,
                editable: sf.editable !== undefined ? !!sf.editable : true,
                locked: !!sf.locked
            });
        }
    });

    return [...base, ...extras];
}

/**
 * Inject property options into the interestedProperty select field.
 * @param {Array} resolvedFields - Array of resolved form field definitions
 * @param {Array} properties - Array of property documents from Firestore
 * @returns {Array} - Updated fields with property options injected
 */
function injectPropertyOptions(resolvedFields, properties = []) {
    if (!Array.isArray(resolvedFields)) return resolvedFields;
    return resolvedFields.map(field => {
        if (field.id !== 'interestedProperty') return field;
        const options = (properties || [])
            .filter(p => p && (p.address || p.name))
            .map(p => ({
                value: p.id,
                label: p.address || p.name || 'Unnamed Property',
            }));
        return { ...field, type: 'multiselect', options };
    });
}

export { BASE_FIELDS, resolveFormFields, injectPropertyOptions };
